import type { ReactNode } from 'react';
import { HelpCircle, Clock, Mail, Sparkles, Database, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import MermaidDiagram from './MermaidDiagram';
import {
  EMAIL_PIPELINE_OVERVIEW_CHART,
  SEND_CRON_QUERY_CHART,
  SEND_STATUS_LIFECYCLE_CHART,
} from './emailPipelineCharts';

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Faq({
  question,
  children,
}: {
  question: string;
  children: ReactNode;
}) {
  return (
    <details className="group rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden">
      <summary className="cursor-pointer list-none px-4 py-3 font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/80 flex items-center gap-2">
        <HelpCircle className="w-4 h-4 text-emerald-500 shrink-0" />
        {question}
      </summary>
      <div className="px-4 pb-4 pt-0 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700/80">
        {children}
      </div>
    </details>
  );
}

const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'send-states', label: 'Send statuses' },
  { id: 'crons', label: 'Background jobs' },
  { id: 'send-cron', label: 'Send cron (every 5 min)' },
  { id: 'newsletter-flow', label: 'Newsletter flow' },
  { id: 'personalization', label: 'AI personalization' },
  { id: 'postmark', label: 'Postmark templates' },
  { id: 'faq', label: 'FAQ' },
];

function StatusTable({
  headers,
  rows,
}: {
  headers: [string, string, string, string];
  rows: string[][];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 not-prose">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((row) => (
            <tr key={row[0]} className="align-top">
              <td className="px-3 py-2 font-mono text-xs text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                {row[0]}
              </td>
              <td className="px-3 py-2">{row[1]}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row[2]}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row[3]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EmailPipelineGuideView() {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800">
            <Mail className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Email send pipeline
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              How newsletters, scheduled sends, AI personalization, and Postmark fit together.
            </p>
          </div>
        </div>
      </div>

      <nav className="mb-8 flex flex-wrap gap-2 text-xs">
        {toc.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="space-y-10">
        <Section id="overview" title="Overview">
          <p>
            Outbound email is driven by rows in <code className="text-xs font-mono">scheduled_emails</code>.
            Each row is one message to one subscriber. Newsletters materialize many rows at once;
            automations create rows per enrollment step.
          </p>
          <MermaidDiagram
            chart={EMAIL_PIPELINE_OVERVIEW_CHART}
            caption="Personalize runs before send. Box ⑤ = async open/click feedback (see tracking guide)."
          />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <strong>Open &amp; click tracking</strong> is a separate loop after Postmark accepts the
            message: webhooks → <code className="text-xs font-mono">email_engagement_events</code> →
            optional daily rollup.{' '}
            <Link
              to="/guide/email-tracking"
              className="text-emerald-600 dark:text-emerald-400 underline font-medium"
            >
              Email tracking guide
            </Link>
            . Background work runs in{' '}
            <Link to="/guide/inngest" className="text-violet-600 dark:text-violet-400 underline font-medium">
              Inngest
            </Link>
            ; HTTP entry points are in the{' '}
            <Link
              to="/guide/edge-functions"
              className="text-orange-600 dark:text-orange-400 underline font-medium"
            >
              edge functions guide
            </Link>
            .
          </p>
        </Section>

        <Section id="send-states" title="Send statuses">
          <p>
            Each outbound message is a row in <code className="text-xs font-mono">scheduled_emails</code>.
            The <code className="text-xs font-mono">status</code> column is the delivery lifecycle (not
            the same as AI personalization or newsletter status).
          </p>

          <MermaidDiagram
            chart={SEND_STATUS_LIFECYCLE_CHART}
            caption="Row lifecycle. Only pending rows are eligible for the send cron."
          />

          <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-2">
            Row status (<code className="font-mono text-xs">scheduled_emails.status</code>)
          </h3>
          <StatusTable
            headers={['Status', 'Meaning', 'Set by', 'When to use / notes']}
            rows={[
              [
                'pending',
                'Queued. Waiting until scheduled_at and until send worker (and AI, if enabled) allows delivery.',
                'Materialize, ad-hoc schedule, automation enroll, or admin sets back to pending.',
                'Default for all new sends. Send cron only queries pending rows where scheduled_at ≤ now.',
              ],
              [
                'sending',
                'Short-lived lock while a worker owns the row and calls Postmark.',
                'Send worker only (optimistic update from pending).',
                'Do not set manually. If stuck here after a crash, set back to pending to retry.',
              ],
              [
                'sent',
                'Postmark accepted the message. sent_at and provider (message id) are set.',
                'Send worker after successful API response.',
                'Terminal. Used for engagement tracking and “sent” counts in the UI.',
              ],
              [
                'failed',
                'Send did not complete. error column has the reason (validation, Postmark, missing email).',
                'Send worker on failure.',
                'Terminal until you fix the issue and set status back to pending (clears error, attempts reset in admin UI).',
              ],
              [
                'canceled',
                'This row will not be sent (e.g. subscriber unsubscribed before send, or automation enrollment canceled).',
                'Send worker or automation cancel flows.',
                'Terminal. Purge newsletter pending deletes pending rows only—not sent/failed.',
              ],
            ]}
          />

          <p className="text-xs text-gray-500 dark:text-gray-400">
            <strong>Inngest-only outcome:</strong> the send worker may return{' '}
            <code className="font-mono">skipped</code> in logs when another process already claimed
            the row or AI personalization blocks send—the database status stays{' '}
            <code className="font-mono">pending</code> until the next tick.
          </p>

          <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-4">
            AI personalization status (
            <code className="font-mono text-xs">personalization.status</code> on the row JSON)
          </h3>
          <p>
            Only exists when <code className="text-xs font-mono">personalization.enabled</code> is
            true (newsletter setting at materialize, or toggled on ad-hoc scheduled email). Does not
            replace row status—you can have row <code className="font-mono">pending</code> with
            personalization <code className="font-mono">completed</code>.
          </p>
          <StatusTable
            headers={['Status', 'Meaning', 'Set by', 'When to use / notes']}
            rows={[
              [
                'pending',
                'AI has not run yet for this row.',
                'Materialize or insert with AI enabled.',
                'Send cron waits—will not Postmark until this leaves pending/processing.',
              ],
              [
                'processing',
                'Personalize worker claimed this row and is calling OpenAI.',
                'Personalize cron only.',
                'Usually seconds. If stuck, next run may retry; send still blocked until completed/skipped/failed.',
              ],
              [
                'completed',
                'Subject/body on the row were updated to tuned copy; result stored in personalization JSON.',
                'Personalize cron after success.',
                'Send cron may proceed when row is also due.',
              ],
              [
                'skipped',
                'Subscriber profile score below min_score—base newsletter copy is kept.',
                'Personalize cron.',
                'Normal for low-profile recipients. Send uses base subject/body.',
              ],
              [
                'failed',
                'OpenAI or worker error. Base copy remains; personalization.error may be set.',
                'Personalize cron.',
                'Send can still go out with base copy once failed (not blocked like pending).',
              ],
            ]}
          />

          <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-4">
            Newsletter status (<code className="font-mono text-xs">newsletters.status</code>)
          </h3>
          <p>Separate from per-recipient send rows—tracks the campaign in the editor:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Draft / On Hold</strong> — Editing; not materialized (or purged back to Draft).
            </li>
            <li>
              <strong>Ready to Publish</strong> — Materialized: pending rows exist; sends are in flight
              or waiting for scheduled time.
            </li>
            <li>
              <strong>Published</strong> — Send time passed and no pending rows left for this
              newsletter.
            </li>
          </ul>

          <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-4">
            Manual status changes (admin UI)
          </h3>
          <p>
            On a subscriber’s scheduled email (Schedule email modal), you can change row status.
            Typical uses:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>pending</strong> — Retry a failed send after fixing content or Postmark issues
              (moving from failed → pending clears error, sent_at, provider, and attempts).
            </li>
            <li>
              Leave as <strong>sent</strong> / <strong>failed</strong> / <strong>canceled</strong>{' '}
              for history—do not set to pending unless you intend a resend.
            </li>
            <li>
              Avoid setting <strong>sending</strong> manually—the worker sets it during delivery.
            </li>
          </ul>
        </Section>

        <Section id="crons" title="Background jobs (Inngest)">
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">Job</th>
                  <th className="px-3 py-2">Schedule</th>
                  <th className="px-3 py-2">What it does</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">process-scheduled-emails</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Clock className="w-3.5 h-3.5 inline mr-1" />
                    Every 5 minutes
                  </td>
                  <td className="px-3 py-2">
                    Sends due pending rows via Postmark (batch default 50;{' '}
                    <code className="text-xs">SCHEDULED_EMAIL_SEND_BATCH_SIZE</code>).
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">personalize-scheduled-emails</td>
                  <td className="px-3 py-2 whitespace-nowrap">Every 15 minutes</td>
                  <td className="px-3 py-2">
                    Runs AI tuning on rows with personalization enabled (batch up to 50).
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-xs">publish-completed-newsletters</td>
                  <td className="px-3 py-2 whitespace-nowrap">Every 5 minutes</td>
                  <td className="px-3 py-2">
                    Sets newsletter status to Published when all sends finished and send time passed.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Ad-hoc events also exist: <code className="text-xs font-mono">scheduled-emails/send.requested</code>{' '}
            (admin Send now on one row),{' '}
            <code className="text-xs font-mono">scheduled-emails/personalize.requested</code> (after
            materialize), and manual triggers for testing.
          </p>
        </Section>

        <Section id="send-cron" title="Send cron — does it look backwards?">
          <p>
            <strong>Yes.</strong> Each run asks: “Which rows are still{' '}
            <code className="text-xs font-mono">pending</code> and whose{' '}
            <code className="text-xs font-mono">scheduled_at</code> is already in the past (≤ now)?”
          </p>
          <MermaidDiagram
            chart={SEND_CRON_QUERY_CHART}
            className="mb-4"
            caption="Each 5-minute run: not a sliding window — any overdue pending row can be picked up."
          />
          <div className="rounded-lg bg-slate-900 text-slate-100 p-4 font-mono text-xs overflow-x-auto">
            {`SELECT … FROM scheduled_emails
WHERE status = 'pending'
  AND scheduled_at <= now()
ORDER BY scheduled_at ASC
LIMIT 50`}
          </div>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Not a sliding window.</strong> It does not only look at emails scheduled in the
              last 5 minutes. Anything overdue and still pending is eligible.
            </li>
            <li>
              <strong>Backlog.</strong> If the worker was down or sends were blocked (e.g. AI still
              running), those rows stay pending until a later tick picks them up (50 per run).
            </li>
            <li>
              <strong>Not early.</strong> Rows with <code className="text-xs font-mono">scheduled_at</code>{' '}
              in the future are ignored until that instant passes.
            </li>
            <li>
              <strong>Delivery timing.</strong> Worst case, a row due at 9:00:00 ships on the first
              cron after that — up to ~5 minutes later unless you use Send now.
            </li>
          </ul>
          <p>
            Before Postmark, the worker skips rows where AI personalization is still{' '}
            <code className="text-xs font-mono">pending</code> or{' '}
            <code className="text-xs font-mono">processing</code>. Those stay in the queue and are
            retried on the next 5-minute tick.
          </p>
        </Section>

        <Section id="newsletter-flow" title="Newsletter flow">
          <ol className="list-decimal pl-5 space-y-3">
            <li>
              <strong>Draft</strong> — Edit content, audience (channels), subject, Postmark template,
              optional AI personalization, and <strong>scheduled send date &amp; time</strong>{' '}
              (stored as timestamptz).
            </li>
            <li>
              <strong>Schedule send / Send now</strong> (confirm modal) — Calls{' '}
              <code className="text-xs font-mono">materialize-newsletter</code>, which renders blocks
              to HTML and inserts one <code className="text-xs font-mono">scheduled_emails</code>{' '}
              row per matching subscriber. Newsletter becomes <strong>Ready to Publish</strong>.
              Send now sets <code className="text-xs font-mono">scheduled_at</code> to now.
            </li>
            <li>
              <strong>Recipients tab</strong> — You can add/import subscribers later; they get rows
              with the same newsletter <code className="text-xs font-mono">scheduled_at</code>{' '}
              (not “now” unless the newsletter time already passed).
            </li>
            <li>
              <strong>Send cron</strong> — Drains pending rows when due.
            </li>
            <li>
              <strong>Published</strong> — After send time passed and no pending rows remain for that
              newsletter.
            </li>
          </ol>
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800/40">
            <Database className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
            <p className="text-xs">
              Purge scheduled (from newsletter UI) deletes pending rows and can return the newsletter
              to Draft. That is the way to “undo” a materialize without sending.
            </p>
          </div>
        </Section>

        <Section id="personalization" title="AI personalization">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-violet-500" />
            <span className="font-medium text-gray-900 dark:text-white">Order: personalize, then send</span>
          </div>
          <p>
            When enabled on the newsletter, each materialized row gets a{' '}
            <code className="text-xs font-mono">personalization</code> JSON blob with{' '}
            <code className="text-xs font-mono">enabled: true</code> and{' '}
            <code className="text-xs font-mono">status: pending</code>.
          </p>
          <p>The personalize worker selects rows where:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>status is still pending (email not sent)</li>
            <li>personalization enabled and status pending</li>
            <li>
              <code className="text-xs font-mono">scheduled_at</code> is within the next{' '}
              <strong>24 hours</strong> (configurable via{' '}
              <code className="text-xs font-mono">AI_PERSONALIZATION_LEAD_HOURS</code>)
            </li>
          </ul>
          <p>
            After a successful run, subject/body on the row are updated and personalization status
            becomes <code className="text-xs font-mono">completed</code> (or{' '}
            <code className="text-xs font-mono">skipped</code> / <code className="text-xs font-mono">failed</code>).
            The send cron will not deliver while status is pending or processing.
          </p>
          <p className="text-xs text-gray-500">
            Preview in the admin UI is dry-run only; production uses the Inngest worker + OpenAI.
            Disable globally with <code className="text-xs font-mono">AI_PERSONALIZATION_GLOBAL_ENABLED=false</code>.
          </p>
        </Section>

        <Section id="postmark" title="Postmark templates &amp; layout">
          <p>
            <strong>Template wrap</strong> — Newsletter HTML goes in{' '}
            <code className="text-xs font-mono">{`{{{body_html}}}`}</code> on a standard Postmark
            template. The template may use a <strong>Layout</strong> (header/footer). Layout and
            standard template share the same <code className="text-xs font-mono">TemplateModel</code>{' '}
            from our API.
          </p>
          <p>Common merge fields we send per recipient:</p>
          <ul className="list-disc pl-5 font-mono text-xs">
            <li>email, name, first_name</li>
            <li>subject, body_html (template_wrap), subtitle (preheader)</li>
            <li>scheduled_email_id — UUID of the send row (footer / support ref)</li>
          </ul>
          <p>
            Use <code className="text-xs font-mono">{`{{scheduled_email_id}}`}</code> in the{' '}
            <strong>layout footer</strong> or the template — both work. If the key is missing from the
            payload, Postmark renders nothing (empty), not the literal tag name.
          </p>
          <p>
            <strong>Raw HTML</strong> mode skips Postmark templates entirely; merge fields do not apply.
          </p>
        </Section>

        <Section id="faq" title="Frequently asked questions">
          <div className="space-y-2 not-prose">
            <Faq question="Why is my email up to 5 minutes late?">
              <p>
                The send worker runs every 5 minutes and only processes rows with{' '}
                <code className="font-mono text-xs">scheduled_at ≤ now</code>. Use Send now on a
                single recipient (or materialize with send now) for immediate queueing; delivery
                still waits for the next cron tick unless Inngest processes the event immediately.
              </p>
            </Faq>
            <Faq question="Will missed cron runs skip emails?">
              <p>
                No. Overdue pending rows remain in the table until sent (or failed/canceled). Each
                run takes the oldest 50 due rows. Large backlogs drain gradually.
              </p>
            </Faq>
            <Faq question="Why is personalization enabled but the email looks generic?">
              <p>
                Check the row’s personalization JSON: status should be completed. If still pending,
                send is blocked. Low profile score subscribers are skipped by design (base newsletter
                only). Confirm OpenAI key and that the global personalization flag is not disabled.
              </p>
            </Faq>
            <Faq question="What is scheduled_at vs newsletters.scheduled_for?">
              <p>
                <code className="font-mono text-xs">newsletters.scheduled_for</code> is the planned
                send time you set in Settings. At materialize, that instant is copied to each{' '}
                <code className="font-mono text-xs">scheduled_emails.scheduled_at</code>. The send
                cron only reads the per-row timestamp.
              </p>
            </Faq>
            <Faq question="Why didn’t {{scheduled_email_id}} show in the footer?">
              <p>
                Only template sends include it. Verify content mode is template_wrap or template_only,
                the Inngest worker is deployed, and the Postmark activity JSON shows{' '}
                <code className="font-mono text-xs">scheduled_email_id</code> in TemplateModel. Use
                exact spelling; in Mustache sections use <code className="font-mono text-xs">{`{{.}}`}</code>{' '}
                inside <code className="font-mono text-xs">{`{{#scheduled_email_id}}`}</code>.
              </p>
            </Faq>
            <Faq question="Can I add recipients after scheduling?">
              <p>
                Yes, while the newsletter is Draft, Ready to Publish, or Published. New rows use the
                newsletter’s scheduled time. They personalize and send on the same rules as the
                original batch.
              </p>
            </Faq>
            <Faq question="What does each send status mean?">
              <p>
                See the <a href="#send-states" className="text-emerald-600 dark:text-emerald-400 underline">Send statuses</a>{' '}
                section: pending → sending → sent/failed, plus canceled. AI has its own
                personalization.status on the JSON blob.
              </p>
            </Faq>
            <Faq question="What’s the difference between Send now on a row vs newsletter Send now?">
              <p>
                Newsletter Send now materializes (or re-stamps) the whole campaign with{' '}
                <code className="font-mono text-xs">scheduled_at = now</code> for all recipients.
                Per-recipient Send now on the Recipients tab only updates that row’s{' '}
                <code className="font-mono text-xs">scheduled_at</code> and queues one Inngest send
                event.
              </p>
            </Faq>
          </div>
        </Section>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <ArrowRight className="w-4 h-4 shrink-0" />
          <span>
            Related tables: <code className="font-mono text-xs">newsletters</code>,{' '}
            <code className="font-mono text-xs">scheduled_emails</code>,{' '}
            <code className="font-mono text-xs">subscribers</code>. Workers live in Supabase edge
            function <code className="font-mono text-xs">inngest</code>.
          </span>
        </div>
      </div>
    </div>
  );
}
